// Admin-only: desktop notification when a new wholesale order is placed (Firestore `orders`).
// Skips reservation stubs (`reserved: true`); dedupes by order id in localStorage.
// Only alerts for orders whose **placed** time falls on the **same calendar day** (Europe/London) as now,
// so sync/invoice edits to old orders never trigger "new order" spam.

import { useEffect, useRef } from 'react';
import { collection, onSnapshot, doc, getDoc } from 'firebase/firestore';
import { useAuth } from '../context/AuthContext';
import { db } from '../services/firebase';
import { getOrderDocumentTimeMs } from '../utils/orderDocumentTime';
import { isSameCalendarDayLondon } from '../utils/adminDesktopNotifyWindow';
import {
    userHasAdminRole,
    showAdminDesktopNotificationIfAllowed,
} from '../services/adminDesktopNotificationService';

const SHOWN_ORDER_IDS_KEY = 'lsc_admin_desktop_shown_order_ids';
const MAX_STORED_IDS = 500;

function loadShownOrderIds() {
    try {
        const raw = localStorage.getItem(SHOWN_ORDER_IDS_KEY);
        if (raw) return new Set(JSON.parse(raw));
    } catch {
        /* ignore */
    }
    return new Set();
}

function rememberOrderId(orderId) {
    try {
        const shown = loadShownOrderIds();
        shown.add(orderId);
        const arr = Array.from(shown);
        const trimmed = arr.slice(-MAX_STORED_IDS);
        localStorage.setItem(SHOWN_ORDER_IDS_KEY, JSON.stringify(trimmed));
    } catch {
        /* ignore */
    }
}

/**
 * True when the document looks like a completed placed order (not an ID reservation stub).
 * @param {Record<string, unknown>} data
 */
function isPlacedOrderDoc(data) {
    if (!data || typeof data !== 'object') return false;
    if (data.reserved === true) return false;
    const items = Array.isArray(data.order_items)
        ? data.order_items
        : Array.isArray(data.items)
          ? data.items
          : [];
    return items.length > 0;
}

/**
 * True only if the order was placed on today's date (London), so legacy doc updates do not alert for old orders.
 */
function isOrderPlacedTodayForNotification(data) {
    const placedMs = getOrderDocumentTimeMs(data);
    if (placedMs == null) return false;
    return isSameCalendarDayLondon(placedMs);
}

function formatOrderBody(orderId, data) {
    const company =
        (data.customer && typeof data.customer === 'object' && data.customer.company) ||
        data.customer_id ||
        'Customer';
    const total = data.order_total != null ? String(data.order_total) : '—';
    return `${company} · #${orderId} · £${total}`;
}

export default function AdminNewOrderDesktopNotifier() {
    const { user } = useAuth();
    const skipFirstSnapshotRef = useRef(true);

    useEffect(() => {
        if (!user?.uid) return undefined;

        let cancelled = false;
        let unsubOrders = null;

        (async () => {
            try {
                const snap = await getDoc(doc(db, 'users', user.uid));
                if (cancelled) return;
                const userData = snap.exists()
                    ? { uid: user.uid, ...snap.data() }
                    : { uid: user.uid, role: 'Customer' };
                if (!userHasAdminRole(userData)) return;

                skipFirstSnapshotRef.current = true;

                unsubOrders = onSnapshot(
                    collection(db, 'orders'),
                    (snapshot) => {
                        if (skipFirstSnapshotRef.current) {
                            skipFirstSnapshotRef.current = false;
                            return;
                        }

                        snapshot.docChanges().forEach((change) => {
                            if (change.type !== 'added' && change.type !== 'modified') return;
                            const orderId = change.doc.id;
                            const data = change.doc.data();
                            if (!isPlacedOrderDoc(data)) return;
                            if (!isOrderPlacedTodayForNotification(data)) return;

                            const shown = loadShownOrderIds();
                            if (shown.has(orderId)) return;

                            rememberOrderId(orderId);

                            const body = formatOrderBody(orderId, data);
                            void (async () => {
                                if (cancelled) return;
                                try {
                                    const uSnap = await getDoc(doc(db, 'users', user.uid));
                                    if (cancelled) return;
                                    const fresh = uSnap.exists()
                                        ? { uid: user.uid, ...uSnap.data() }
                                        : { uid: user.uid, role: 'Customer' };
                                    if (!userHasAdminRole(fresh)) return;
                                    showAdminDesktopNotificationIfAllowed(fresh, 'New order', body, {
                                        tag: `lsc-order-${orderId}`,
                                        onClickNavigate: '/admin',
                                    });
                                } catch (e) {
                                    console.warn('[AdminNewOrderDesktopNotifier] role re-check', e);
                                }
                            })();
                        });
                    },
                    (err) => {
                        console.error('[AdminNewOrderDesktopNotifier]', err);
                    }
                );
            } catch (e) {
                console.error('[AdminNewOrderDesktopNotifier] setup', e);
            }
        })();

        return () => {
            cancelled = true;
            if (unsubOrders) unsubOrders();
        };
    }, [user]);

    return null;
}
